import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, BookOpen, Loader2, Phone } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { fetchGuide } from '../lib/api';

const GuideDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [guide, setGuide] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setError(false);
    fetchGuide(id)
      .then(setGuide)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32 text-[#5a5a5a]">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  if (error || !guide) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h2 className="font-serif-besley text-3xl text-[#0F1B2D] mb-4">Guide not found</h2>
        <p className="text-[#5a5a5a] mb-6">The article you were looking for doesn&apos;t exist or has been moved.</p>
        <button
          onClick={() => navigate('/legal-guides')}
          className="btn-orange inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium"
        >
          <ArrowLeft className="w-4 h-4" /> Back to all guides
        </button>
      </div>
    );
  }

  return (
    <div>
      <section className="bg-[#0F1B2D] text-[#F8F2E4] py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <Link to="/legal-guides" className="inline-flex items-center gap-2 text-[#E89530] text-sm mb-8 hover:underline underline-offset-4">
            <ArrowLeft className="w-4 h-4" /> All guides
          </Link>
          <div>
            <Badge variant="secondary" className="bg-[#E89530]/15 text-[#E89530] border border-[#E89530]/30 mb-5">
              {guide.category}
            </Badge>
          </div>
          <h1 className="font-serif-besley text-4xl md:text-5xl lg:text-6xl leading-[1.1] mb-6">
            {guide.title}
          </h1>
          <p className="text-[#C9BFA8] text-lg max-w-3xl mb-6 leading-relaxed">{guide.excerpt}</p>
          <div className="flex items-center gap-2 text-sm text-[#C9BFA8]">
            <Clock className="w-4 h-4 text-[#E89530]" /> {guide.readTime}
          </div>
        </div>
      </section>

      <section className="bg-[#F8F2E4] py-16">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <article className="space-y-10">
            {(guide.sections || []).map((s) => (
              <div key={s.heading}>
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-9 h-9 rounded-lg bg-[#EDE3CC] flex items-center justify-center shrink-0">
                    <BookOpen className="w-4 h-4 text-[#0F1B2D]" strokeWidth={1.8} />
                  </div>
                  <h2 className="font-serif-besley text-2xl md:text-3xl text-[#0F1B2D] leading-tight pt-1">
                    {s.heading}
                  </h2>
                </div>
                <p className="text-[16px] text-[#2a2a2a] leading-[1.8] pl-12">{s.body}</p>
              </div>
            ))}
          </article>

          <div className="mt-14 p-8 rounded-2xl bg-[#EDE3CC] flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h3 className="font-serif-besley text-2xl text-[#0F1B2D] mb-2">Need personal help with this?</h3>
              <p className="text-[#3a3a3a] text-sm">Speak to a verified advocate or request free legal aid.</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/find-a-lawyer"
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium border border-[#0F1B2D]/30 text-[#0F1B2D] hover:border-[#0F1B2D] transition-colors"
              >
                Find a lawyer
              </Link>
              <Link
                to="/free-aid"
                className="btn-orange inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium"
              >
                <Phone className="w-4 h-4" /> Request free aid
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GuideDetail;
